// VRG-706 — one row of `GET /companies/admin` (what `useCompaniesQuery` returns) is NOT a
// company. It is a list ROW, `{ company: {id, name, …}, plan, certificates_count, … }`
// (`CompanyListRow` in types/Company.ts, mirroring the API's get-company-list.response.dto.ts).
//
// Until VRG-706 the hook typed those rows as `Company[]`, so `row.id` and `row.name`
// type-checked and were `undefined` at runtime. The tracked-link modal trusted that type: its
// company picker listed one blank row per company, and picking one sent
// `company_id: "undefined"`, which the API refuses with 400 "Unknown company". Nine other
// callers had each hand-rolled `entry.company ?? entry`. Every picker now goes through this.
//
// Pure, no app imports: the platform guard (test/listed-company.guard.spec.ts) loads it under
// node's own test runner. So it is generic over the company type rather than importing it.

/** What every option needs: an id that is a real string, and a name a reader can see. */
export type ListedCompany = { id: string; name: string }

type CompanyLike = { id?: unknown; name?: unknown }
type ListRowLike<C> = { company?: C | null }

const UNNAMED = 'Unnamed company'

/**
 * The company inside one `/companies/admin` row, with every field it carries (logo,
 * subdomain, …), `id` guaranteed and `name` never blank ("Unnamed company").
 *
 * Null for a row without a string id, so a malformed row is dropped instead of becoming an
 * option worth "undefined". A bare company (no `company` key) is still accepted at runtime,
 * the fallback every older call site kept.
 */
export const listedCompany = <C extends CompanyLike>(
  row: ListRowLike<C> | null | undefined,
): (Omit<C, 'id' | 'name'> & ListedCompany) | null => {
  if (!row || typeof row !== 'object') return null
  const c = (row.company && typeof row.company === 'object' ? row.company : row) as C
  if (typeof c.id !== 'string' || !c.id) return null
  const name = typeof c.name === 'string' && c.name.trim() ? c.name : UNNAMED
  return { ...c, id: c.id, name }
}

/** Every company in a `/companies/admin` page, in the order the API sent them. */
export const listedCompanies = <C extends CompanyLike>(
  response: { data?: ReadonlyArray<ListRowLike<C> | null | undefined> | null } | null | undefined,
): Array<Omit<C, 'id' | 'name'> & ListedCompany> => {
  const rows = Array.isArray(response?.data) ? response.data : []
  const out: Array<Omit<C, 'id' | 'name'> & ListedCompany> = []
  for (const row of rows) {
    const c = listedCompany<C>(row)
    if (c) out.push(c)
  }
  return out
}

/**
 * Picker options for a company field: the listed companies, plus `current` when it is not
 * among them (a person's company can be a demo or archived one the list leaves out). Without
 * that, a pre-filled value matches no option and the trigger shows nothing, or a raw id.
 */
export const companyPickerOptions = (
  companies: ListedCompany[],
  current?: ListedCompany | null,
): Array<{ value: string; label: string }> => {
  const options = companies.map((c) => ({ value: c.id, label: c.name }))
  if (current?.id && !companies.some((c) => c.id === current.id)) {
    options.unshift({ value: current.id, label: current.name || UNNAMED })
  }
  return options
}
